import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { NavLink, useLocation } from 'react-router-dom';
import { api } from '@/lib/api';
import { color, font, fontSize, fontWeight, radius, layout, motion, zIndex } from '@/design/tokens';
import { useI18n, useT } from '@/i18n';
import { BrandMark } from '@/design/BrandMark';
import { useSession } from '@/lib/session';
import { NAV_ITEMS, PLATFORM_NAV_ITEMS, SCREEN_INDEX } from './navigation';
import type { NavItem } from './navigation';
import { CommandPalette } from './CommandPalette';
import { NotificationDrawer } from './NotificationDrawer';
import { IdleTimeoutModal } from '@/features/auth/IdleTimeoutModal';

/**
 * The authenticated application shell (DS-004).
 *
 * Transcribed from `design/MedOS.dc.html`: 250px navy sidebar with the branch
 * switcher and role-filtered navigation, a 60px sticky top bar carrying global
 * search and notifications, and the impersonation banner above everything when
 * platform support is inside a tenant.
 *
 * Navigation is filtered by permission rather than by role. A receptionist who
 * has been granted `finance.read` sees the finance entries without this file
 * changing (RBAC-007).
 */

const initialsOf = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0] ?? '')
    .join(' ');

function SidebarLink({ item, badge }: { item: NavItem; badge?: number }) {
  const t = useT();
  return (
    <NavLink
      to={item.path}
      style={({ isActive }) => ({
        display: 'flex',
        alignItems: 'center',
        gap: '11px',
        padding: '9px 11px',
        borderRadius: radius.md,
        fontSize: '13.5px',
        cursor: 'pointer',
        textDecoration: 'none',
        transition: `background ${motion.fast} ${motion.easing}`,
        ...(item.separator
          ? {
              marginTop: '10px',
              paddingTop: '15px',
              borderTop: `1px solid ${color.border.onDark}`,
              borderRadius: `0 0 ${radius.md} ${radius.md}`,
            }
          : null),
        ...(isActive
          ? {
              background: color.brand.primary,
              fontWeight: fontWeight.bold,
              color: color.text.onDark,
            }
          : { color: color.text.onDarkItem, fontWeight: fontWeight.medium }),
      })}
    >
      {({ isActive }) => (
        <>
          <span
            aria-hidden
            style={{ width: '18px', textAlign: 'center', fontSize: fontSize.base, opacity: 0.9 }}
          >
            {item.icon}
          </span>
          <span style={{ flex: 1 }}>{t(item.labelKey)}</span>
          {badge ? (
            <span
              style={{
                fontSize: fontSize.xs,
                fontWeight: fontWeight.bold,
                padding: '1px 7px',
                borderRadius: radius.pill,
                ...(isActive
                  ? { background: 'rgba(255,255,255,.22)' }
                  : { background: 'rgba(255,255,255,.1)', color: color.text.onDarkMuted }),
              }}
            >
              {badge}
            </span>
          ) : null}
        </>
      )}
    </NavLink>
  );
}

export interface ShellBadges {
  appointmentsToday?: number;
  waiting?: number;
  lab?: number;
  imaging?: number;
  readyCheckout?: number;
}

export function AppShell({ children, badges = {} }: { children: ReactNode; badges?: ShellBadges }) {
  const t = useT();
  // `n` renders the badge count in the tenant's numeral system: an Arabic UI
  // shows ٣, not 3 (CONVENTIONS §8).
  const { locale, setLocale, n } = useI18n();
  const { user, impersonation, activeBranchId, setActiveBranch, signOut } = useSession();
  const [searchOpen, setSearchOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [indexOpen, setIndexOpen] = useState(false);
  const [branchOpen, setBranchOpen] = useState(false);
  const location = useLocation();

  /**
   * Unread badge (NOTIF-006).
   *
   * Same recipient and state model as the drawer's list, resolved server-side,
   * so the number on the bell and the rows behind it cannot disagree. Polled
   * rather than pushed: the realtime channel is an optional dependency and the
   * badge has to be right without it (FLOW-009, PRD 21.1).
   */
  const { data: unread } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => api.get<{ count: number; critical: number }>('/notifications/unread-count'),
    refetchInterval: 60_000,
    // A failed poll must not blank a badge the user is relying on.
    placeholderData: (previous) => previous,
    enabled: Boolean(user?.permissions.includes('notifications.read')),
  });
  const unreadCount = unread?.count ?? 0;

  const items = useMemo(() => {
    if (!user) return [];
    const source =
      user.isPlatformAdmin && location.pathname.startsWith('/platform')
        ? PLATFORM_NAV_ITEMS
        : NAV_ITEMS;
    return source.filter((item) => {
      if (!item.permission) return true;
      const anyOf: readonly string[] = Array.isArray(item.permission)
        ? item.permission
        : [item.permission];
      return anyOf.some((p) => user.permissions.includes(p as never));
    });
  }, [user, location.pathname]);

  if (!user) return null;

  const activeBranch = user.branches.find((b) => b.id === activeBranchId) ?? user.branches[0];

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: color.surface.page }}>
      <a href="#main" className="skip-link">
        {t('app.skipToContent')}
      </a>

      {/* ---- Sidebar ---------------------------------------------------- */}
      <nav
        aria-label={t('app.name')}
        style={{
          width: layout.sidebarWidth,
          flexShrink: 0,
          background: color.brand.secondary,
          color: color.text.onDark,
          display: 'flex',
          flexDirection: 'column',
          position: 'sticky',
          top: 0,
          height: '100vh',
        }}
      >
        <div
          style={{
            padding: '20px 20px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: '11px',
            borderBottom: `1px solid ${color.border.onDark}`,
          }}
        >
          {/* No teal "M" tile any more. The ring with the pulse through it is
              the "o" in Clinvis-o-r, so the wordmark already carries the mark —
              a tile beside it would be the same device twice. `ringColor` is
              the pale tint because the teal ring does not separate from this
              navy sidebar. */}
          <div style={{ minWidth: 0 }}>
            <BrandMark size={fontSize.xl} ringColor={color.brand.soft} />
            <div
              style={{
                fontSize: fontSize.xs,
                color: color.text.onDarkMuted,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {user.tenantName}
            </div>
          </div>
        </div>

        {/* Branch switcher. Hidden for single-branch clinics: a control with one
            option is noise, and PRD 7.4 only requires the active branch be
            visible "where relevant". */}
        {user.branches.length > 0 ? (
          <div style={{ padding: '12px 14px 10px', position: 'relative' }}>
            <button
              type="button"
              onClick={() => user.branches.length > 1 && setBranchOpen((v) => !v)}
              aria-expanded={branchOpen}
              aria-haspopup={user.branches.length > 1}
              disabled={user.branches.length <= 1}
              style={{
                width: '100%',
                background: 'rgba(255,255,255,.07)',
                borderRadius: radius.md,
                padding: '9px 11px',
                display: 'flex',
                alignItems: 'center',
                gap: '9px',
                border: 'none',
                color: 'inherit',
                fontFamily: 'inherit',
                cursor: user.branches.length > 1 ? 'pointer' : 'default',
                textAlign: 'start',
              }}
            >
              <span
                aria-hidden
                style={{
                  width: '7px',
                  height: '7px',
                  borderRadius: '50%',
                  background: color.accent.online,
                }}
              />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span
                  style={{
                    display: 'block',
                    fontSize: fontSize.xxs,
                    color: color.text.onDarkMuted,
                  }}
                >
                  {t('app.activeBranch')}
                </span>
                <span
                  style={{ display: 'block', fontSize: fontSize.base, fontWeight: fontWeight.bold }}
                >
                  {activeBranch
                    ? locale === 'ar'
                      ? activeBranch.nameAr
                      : (activeBranch.nameEn ?? activeBranch.nameAr)
                    : '—'}
                </span>
              </span>
              {user.branches.length > 1 ? (
                <span aria-hidden style={{ color: color.text.onDarkMuted, fontSize: fontSize.xs }}>
                  ▾
                </span>
              ) : null}
            </button>

            {branchOpen ? (
              <ul
                role="listbox"
                style={{
                  position: 'absolute',
                  insetInline: '14px',
                  marginTop: '4px',
                  background: color.surface.card,
                  color: color.text.primary,
                  borderRadius: radius.md,
                  boxShadow: '0 12px 32px rgba(15,23,42,.28)',
                  padding: '6px',
                  listStyle: 'none',
                  zIndex: zIndex.topbar + 1,
                }}
              >
                {user.branches.map((branch) => (
                  <li key={branch.id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={branch.id === activeBranchId}
                      onClick={() => {
                        setActiveBranch(branch.id);
                        setBranchOpen(false);
                      }}
                      style={{
                        width: '100%',
                        textAlign: 'start',
                        padding: '9px 11px',
                        borderRadius: radius.sm,
                        border: 'none',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        fontSize: fontSize.base,
                        background: branch.id === activeBranchId ? color.brand.soft : 'transparent',
                        color:
                          branch.id === activeBranchId ? color.brand.primary : color.text.primary,
                        fontWeight:
                          branch.id === activeBranchId ? fontWeight.bold : fontWeight.medium,
                      }}
                    >
                      {locale === 'ar' ? branch.nameAr : (branch.nameEn ?? branch.nameAr)}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}

        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '4px 10px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
          }}
        >
          {items.map((item) => (
            <SidebarLink
              key={`${item.id}-${item.path}`}
              item={item}
              badge={item.badgeKey ? badges[item.badgeKey] : undefined}
            />
          ))}
        </div>

        <div
          style={{
            padding: '12px 14px',
            borderTop: `1px solid ${color.border.onDark}`,
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <div
            aria-hidden
            style={{
              width: '32px',
              height: '32px',
              borderRadius: radius.md,
              background: 'rgba(255,255,255,.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: fontSize.sm,
              fontWeight: fontWeight.bold,
            }}
          >
            {initialsOf(user.displayName)}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: fontSize.base,
                fontWeight: fontWeight.bold,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {user.displayName}
            </div>
            <div style={{ fontSize: fontSize.xs, color: color.text.onDarkMuted }}>
              {t(`role.${user.role}`)}
            </div>
          </div>
          <button
            type="button"
            onClick={() => void signOut()}
            title={t('app.signOut')}
            aria-label={t('app.signOut')}
            style={{
              background: 'none',
              border: 'none',
              color: color.text.onDarkMuted,
              cursor: 'pointer',
              fontSize: fontSize.md,
              padding: '4px',
            }}
          >
            ⏻
          </button>
        </div>
      </nav>

      {/* ---- Main column ------------------------------------------------ */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {impersonation ? (
          <div
            role="alert"
            style={{
              background: color.accent.impersonationBg,
              color: color.accent.impersonationFg,
              padding: '9px 24px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              fontSize: fontSize.base,
              fontWeight: fontWeight.bold,
              flexWrap: 'wrap',
            }}
          >
            <span
              aria-hidden
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: color.accent.impersonationDot,
              }}
            />
            <span>{t('impersonation.title', { tenant: impersonation.tenantName })}</span>
            <span style={{ opacity: 0.8, fontWeight: fontWeight.medium }}>
              {t('impersonation.purpose', { purpose: impersonation.purpose })}
            </span>
          </div>
        ) : null}

        <header
          style={{
            height: layout.topbarHeight,
            background: color.surface.card,
            borderBottom: `1px solid ${color.border.default}`,
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            padding: '0 24px',
            position: 'sticky',
            top: 0,
            zIndex: zIndex.topbar,
          }}
        >
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            style={{
              flex: 1,
              maxWidth: '440px',
              display: 'flex',
              alignItems: 'center',
              gap: '9px',
              background: color.surface.muted,
              border: `1px solid ${color.border.muted}`,
              borderRadius: radius.md,
              padding: '8px 12px',
              cursor: 'pointer',
              fontFamily: 'inherit',
              textAlign: 'start',
              minHeight: '38px',
            }}
          >
            <span aria-hidden style={{ color: color.text.muted, fontSize: fontSize.base }}>
              ⌕
            </span>
            <span style={{ color: color.text.faint, fontSize: fontSize.base, flex: 1 }}>
              {t('app.search.placeholder')}
            </span>
            <kbd
              dir="ltr"
              style={{
                fontFamily: font.mono,
                fontSize: fontSize.xs,
                color: color.text.faint,
                background: color.surface.card,
                border: `1px solid ${color.border.muted}`,
                borderRadius: radius.xs,
                padding: '1px 6px',
              }}
            >
              ⌘K
            </kbd>
          </button>

          <div style={{ flex: 1 }} />

          <button
            type="button"
            onClick={() => setLocale(locale === 'ar' ? 'en' : 'ar')}
            style={{
              padding: '7px 12px',
              border: `1px solid ${color.border.default}`,
              borderRadius: radius.sm,
              fontSize: fontSize.sm,
              fontWeight: fontWeight.bold,
              cursor: 'pointer',
              background: color.surface.card,
              color: color.text.secondary,
              fontFamily: 'inherit',
            }}
            aria-label={locale === 'ar' ? 'Switch to English' : 'التبديل إلى العربية'}
          >
            {locale === 'ar' ? 'EN' : 'ع'}
          </button>

          <button
            type="button"
            onClick={() => setNotifOpen(true)}
            aria-label={
              unreadCount > 0
                ? `${t('app.notifications')} (${n(unreadCount)})`
                : t('app.notifications')
            }
            style={{
              position: 'relative',
              cursor: 'pointer',
              padding: '7px 9px',
              borderRadius: radius.sm,
              border: 'none',
              background: 'transparent',
            }}
          >
            <span
              aria-hidden
              style={{
                fontSize: fontSize.lg,
                color: color.status.neutral.fg,
                fontWeight: fontWeight.bold,
              }}
            >
              ⚑
            </span>
            {unreadCount > 0 ? (
              // aria-hidden: the count is already in the button's accessible
              // name above, and announcing it twice is worse than not at all.
              <span
                aria-hidden
                style={{
                  position: 'absolute',
                  insetBlockStart: '2px',
                  insetInlineEnd: '2px',
                  minWidth: '17px',
                  padding: '0 4px',
                  borderRadius: radius.pill,
                  background: color.status.danger.fg,
                  color: color.text.onDark,
                  fontSize: fontSize.xxs,
                  fontWeight: fontWeight.black,
                  lineHeight: '17px',
                  textAlign: 'center',
                }}
              >
                {unreadCount > 99 ? '99+' : n(unreadCount)}
              </span>
            ) : null}
          </button>

          <button
            type="button"
            onClick={() => setIndexOpen(true)}
            style={{
              padding: '7px 12px',
              border: `1px solid ${color.border.default}`,
              borderRadius: radius.sm,
              fontSize: fontSize.sm,
              fontWeight: fontWeight.bold,
              cursor: 'pointer',
              color: color.brand.primary,
              background: color.surface.card,
              fontFamily: 'inherit',
            }}
          >
            {t('app.allScreens')}
          </button>
        </header>

        <main id="main" style={{ flex: 1, minWidth: 0, padding: '26px 28px 60px' }}>
          {children}
        </main>
      </div>

      <CommandPalette open={searchOpen} onClose={() => setSearchOpen(false)} />
      <NotificationDrawer open={notifOpen} onClose={() => setNotifOpen(false)} />
      <ScreenIndexModal open={indexOpen} onClose={() => setIndexOpen(false)} />
      {/*
        AUTH-010. Mounted once, here, because it must be reachable from every
        authenticated screen - not from any one feature route. It renders null
        until the server says the idle window is inside its warning threshold.
      */}
      <IdleTimeoutModal />
    </div>
  );
}

/**
 * The "all screens" overlay from the design.
 *
 * Retained for build-out and pilot: it is the only surface that reaches screens
 * with no navigation entry (patient detail, arrival, TV mode, state gallery).
 */
function ScreenIndexModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  if (!open) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,.55)',
        zIndex: zIndex.modal,
        overflowY: 'auto',
        padding: '40px 0',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="فهرس الشاشات"
        style={{
          width: '1080px',
          maxWidth: '94vw',
          margin: '0 auto',
          background: color.surface.card,
          borderRadius: radius['3xl'],
          boxShadow: '0 30px 80px rgba(15,23,42,.35)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '22px 28px',
            borderBottom: `1px solid ${color.border.subtle}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <div style={{ fontSize: fontSize['3xl'], fontWeight: fontWeight.black }}>
              فهرس الشاشات
            </div>
            <div style={{ fontSize: fontSize.base, color: color.text.muted, marginTop: '3px' }}>
              اضغط أي شاشة للانتقال إليها مباشرة
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="إغلاق"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: color.text.muted,
              fontSize: fontSize.xl,
            }}
          >
            ✕
          </button>
        </div>
        <div
          style={{
            padding: '22px 28px 32px',
            display: 'flex',
            flexDirection: 'column',
            gap: '22px',
          }}
        >
          {SCREEN_INDEX.map((group) => (
            <div key={group.titleKey}>
              <div
                style={{
                  fontSize: fontSize.sm,
                  fontWeight: fontWeight.black,
                  color: color.brand.primary,
                  letterSpacing: '.08em',
                  marginBottom: '10px',
                }}
              >
                {group.title}
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))',
                  gap: '9px',
                }}
              >
                {group.items.map((item) => (
                  <NavLink
                    key={`${item.id}-${item.path}`}
                    to={item.path.replace(/:\w+/g, 'demo')}
                    onClick={onClose}
                    style={{
                      padding: '10px 12px',
                      borderRadius: radius.md,
                      fontSize: fontSize.base,
                      border: `1px solid ${color.border.muted}`,
                      background: color.surface.card,
                      fontWeight: fontWeight.medium,
                      color: color.text.primary,
                      textDecoration: 'none',
                    }}
                  >
                    {item.label}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
